/**
 * refusPassager — le réessai borné d'un appel mutant que l'éditeur refuse parce
 * qu'un export du document est en vol (#1860, règle commune n°5 de la spec #1858).
 *
 * ## Le refus, tel qu'il est mesuré
 *
 * Tout export — enregistrement automatique, téléchargement, lecture de la forme —
 * fait refuser les écritures par le moteur docx, alors que `documentMode` vaut
 * toujours `editing`. Tous les chiffres de ce module viennent de sondes du banc
 * `mesures` (`tests/ws-mocked/`), jouées sur l'éditeur réel ; les autres
 * documents y renvoient au lieu de les recopier.
 *
 * `mesure-refus-passager-pendant-export.spec.ts` (2026-10-02, petit acte) :
 *
 * - **Deux formes de refus, toutes deux RENDUES, jamais jetées** (R1, R6). Les
 *   opérations d'écriture relevées rendent `{ success:false, failure:{ code:
 *   'PERMISSION_DENIED', details:{ reason:'review-surface-read-only' } } }`.
 *   `history.undo` et `history.redo` rendent une AUTRE forme : `{ noop:true,
 *   status:'rejected', diagnosticCode:'review-surface-read-only' }`, sans
 *   `success` ni `failure`.
 * - **`viewing` rend le même code et la même raison** (R2). Ce qui distingue la
 *   lecture seule durable du refus passager, c'est le mode relu — pas le refus.
 * - **Un refus n'écrit rien** : la révision du document ne bouge pas (R4).
 * - **Les lectures passent** (`blocks.list`, `info`, `capabilities.get`,
 *   `query.match`, aperçu d'un plan — R1) : seul l'appel mutant est à réessayer.
 * - **Durées** (R3) : un export dure 8 à 15 ms sur le document propre, environ
 *   190 ms juste après une écriture ; l'écriture a alors repassé 216 ms après le
 *   départ de l'export, au bout de 17 refus. C'est la plus longue fenêtre de
 *   refus relevée par cette sonde.
 * - **La fenêtre peut déborder la promesse d'export** (R3 : 1 refus sur 6 à
 *   l'écriture lancée dès sa résolution). Attendre cette promesse ne suffit pas.
 * - **Rejouer un plan atomique déjà appliqué est refusé** (`REVISION_MISMATCH`,
 *   R3) : le verrou optimiste couvre un rejeu qui serait de trop.
 *
 * `mesure-source-unique-export.spec.ts` (S1a) : les refus relevés tombent tous
 * dans la fenêtre de l'export de l'enregistrement automatique, environ 229 ms.
 *
 * `mesure-gros-acte.spec.ts` : `doc.export.toDocx()` dure de 0,4 à 1,2 s sur un
 * acte de 1,5 Mo. ⚠️ C'est une durée d'EXPORT : la fenêtre de refus n'a pas été
 * mesurée sur un gros acte.
 *
 * ## Pourquoi ici, et pas outil par outil
 *
 * Tous les verbes d'écriture passent par `ctx.mutate` (`fencesDeMutation.ts`).
 * Le réessai y est posé une fois ; un réessai par exécuteur serait celui qu'on
 * oublierait d'écrire pour le verbe suivant.
 *
 * ## Deux exclusions, et pourquoi
 *
 * - **`write_markdown`** n'écrit pas par un appel fournisseur unique : il passe
 *   par le point d'écriture des actes (`editor/canvasWritePoint.ts`), qui a sa
 *   propre reprise par tranche, plus ancienne. Les deux partagent ce module pour
 *   reconnaître le refus (`refusPassagerSousCeMode`) et pour le nommer au reçu.
 * - **`set_letterhead`** n'est pas réessayé et son reçu ne nomme pas la cause.
 *   Sa fermeture enchaîne plusieurs appels et rend un code métier
 *   (`editor/canvasLetterhead.ts` réduit chaque refus du fournisseur à son seul
 *   `code`) : la raison n'arrive pas jusqu'à l'exécuteur, et la rejouer
 *   reposerait ce qui a déjà abouti. L'outil est en diagnostic séparé (#1871).
 *
 * ## Ce qui est rejoué : UN appel fournisseur refusé, jamais un lot
 *
 * La fermeture passée à `ctx.mutate` porte un seul appel fournisseur. Elle n'est
 * rejouée que si sa valeur de retour EST le refus passager — donc que rien n'a
 * été écrit. Une fermeture qui enchaîne plusieurs appels rend un résultat à
 * elle : elle n'est jamais rejouée. Une erreur JETÉE n'est jamais rejouée non
 * plus : on ne sait pas ce qu'elle a laissé derrière elle.
 *
 * ## La borne est porteuse
 *
 * Elle garantit qu'on RÉPOND à l'agent. Le budget d'attente vaut pour l'action
 * entière, pas par appel : une action de deux cents écritures qui retomberait
 * deux cents fois sur la fenêtre n'attend pas deux cents bornes. Une fois le
 * budget épuisé, les refus suivants de la même action sont rendus sur-le-champ.
 */

import type { CanvasDocumentMode } from '@hector/shared-types';
import type { PeutEtreUnePromesse } from './executionAsynchrone';

/**
 * Raisons du fournisseur qui désignent un état transitoire.
 *
 * Liste **fermée à dessein** : élargir au code générique `PERMISSION_DENIED`
 * ferait retenter des refus qui sont de vraies permissions — et une boucle de
 * reprise sur un refus définitif est pire que le refus.
 */
const RAISONS_PASSAGERES: ReadonlySet<string> = new Set(['review-surface-read-only']);

/**
 * Attente totale accordée aux refus passagers d'UNE action, en millisecondes.
 *
 * Encadrée des deux côtés, par des valeurs dont l'en-tête de ce module donne la
 * sonde :
 * - vingt fois la plus longue fenêtre de refus mesurée (216 à 229 ms), et
 *   quatre fois la plus longue durée d'export mesurée (1,2 s sur un acte de
 *   1,5 Mo — dont la fenêtre de refus, elle, n'a pas été mesurée) ;
 * - ⚠️ nettement SOUS les bornes du courtier backend pour une écriture
 *   (`SuperDocCanvasTools.ts` : `MUTATION_INACTIVITY_BEFORE_PROBE_MS`, 15 s de
 *   silence avant la sonde passive ; `MUTATION_TIMEOUT_MS`, 20 s pour une
 *   mutation ciblée, la plus courte des bornes d'écriture). Les dépasser
 *   transformerait un refus net — « rien n'a été écrit, réessaie » — en
 *   expiration côté agent, dont l'effet est inconnu et qui interdit tout
 *   rejeu. Ne pas relever cette valeur sans relire ces deux-là.
 */
export const BUDGET_D_ATTENTE_DU_REFUS_PASSAGER_MS = 5_000;

/**
 * Pauses avant chaque reprise. La première est courte parce qu'un export d'un
 * petit acte propre dure une dizaine de millisecondes ; la dernière se répète
 * jusqu'à épuisement du budget.
 */
const PAUSES_AVANT_REPRISE_MS: readonly number[] = [50, 100, 200, 400];

/** Le code que porte le reçu quand un export a empêché une écriture — lu par l'agent. */
export const CODE_ENREGISTREMENT_EN_COURS = 'CANVAS_SAVE_IN_PROGRESS';

/**
 * Comment les refus passagers d'une action se sont dénoués.
 *
 * - `recovered` — au moins un refus, et chaque appel refusé a fini par ABOUTIR.
 * - `other_failure` — après un refus passager, la reprise a rendu un autre
 *   échec du fournisseur (par exemple une révision périmée). Le refus passager
 *   n'est plus la cause : c'est l'exécuteur qui dit ce qui s'est passé.
 * - `bound_exceeded` — le budget d'attente a été épuisé : un refus passager a
 *   été rendu à l'exécuteur.
 * - `durable_read_only` — le mode relu n'était pas inscriptible : le refus n'est
 *   pas passager, il a été rendu sans attendre.
 */
export type IssueDesRefusPassagers = 'recovered' | 'other_failure' | 'bound_exceeded' | 'durable_read_only';

/** Du moins grave au plus grave : une action garde la plus grave de ses issues. */
const GRAVITE: Record<IssueDesRefusPassagers, number> = {
  recovered: 0,
  other_failure: 1,
  bound_exceeded: 2,
  durable_read_only: 3,
};

/** Ce que l'action a vécu, pour le reçu et le diagnostic. */
export interface BilanDesRefusPassagers {
  /** `null` : aucun refus passager — le cas nominal. */
  readonly issue: IssueDesRefusPassagers | null;
  /** Nombre d'appels refusés, reprises comprises. */
  readonly refus: number;
  /** Temps passé à attendre entre deux reprises. */
  readonly attenteMs: number;
  /** Appels mutants dont le fournisseur a rendu autre chose qu'un refus passager. */
  readonly appelsAboutis: number;
}

/** Le réessai d'UNE action : il porte son budget et son bilan. */
export interface ReessaiSurRefusPassager {
  /**
   * Exécute un appel mutant et le rejoue tant que le fournisseur rend le refus
   * passager, dans la limite du budget de l'action.
   *
   * @param tentative - UN essai complet de l'appel. Rappelée à chaque reprise.
   */
  executer: <T>(tentative: () => PeutEtreUnePromesse<T>) => Promise<T>;
  bilan: () => BilanDesRefusPassagers;
}

function estUnObjet(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null;
}

/**
 * La raison du refus si `resultat` est l'un des deux refus passagers que rend le
 * fournisseur, `undefined` sinon.
 *
 * ⚠️ Étroit à dessein : `PERMISSION_DENIED` seul ne suffit pas — c'est aussi le
 * code d'une vraie permission. Et ⚠️ aveugle au mode : `viewing` rend la même
 * valeur. Qui décide d'attendre passe par {@link refusPassagerSousCeMode}.
 */
function raisonDuRefusPassager(resultat: unknown): string | undefined {
  if (!estUnObjet(resultat)) return undefined;
  // Forme des opérations d'écriture : `{ success:false, failure:{ details:{ reason } } }`.
  if (resultat.success === false && estUnObjet(resultat.failure) && estUnObjet(resultat.failure.details)) {
    const raison = resultat.failure.details.reason;
    if (typeof raison === 'string' && RAISONS_PASSAGERES.has(raison)) return raison;
  }
  // Forme de `history.undo` / `history.redo` : ni `success` ni `failure`.
  if (resultat.status === 'rejected' && typeof resultat.diagnosticCode === 'string'
    && RAISONS_PASSAGERES.has(resultat.diagnosticCode)) {
    return resultat.diagnosticCode;
  }
  return undefined;
}

/**
 * La raison du refus si `resultat` est un refus passager QU'IL VAUT D'ATTENDRE,
 * `undefined` sinon.
 *
 * Seuls `editing` et `suggesting` autorisent l'attente : `viewing` est une
 * lecture seule durable, et `unknown` un mode qu'on n'a pas su lire — un signal
 * absent n'est pas un feu vert pour attendre.
 *
 * LE prédicat partagé par les deux reprises du canvas : ce module, et le point
 * d'écriture des actes (`editor/canvasDocumentWrite.ts`).
 *
 * @param lireLeMode - Lecture FRAÎCHE du mode. Appelée seulement si `resultat`
 *   est un refus passager : le cas nominal ne lit rien.
 */
export function refusPassagerSousCeMode(
  resultat: unknown,
  lireLeMode: () => CanvasDocumentMode,
): string | undefined {
  const raison = raisonDuRefusPassager(resultat);
  if (raison === undefined) return undefined;
  const mode = lireLeMode();
  return mode === 'editing' || mode === 'suggesting' ? raison : undefined;
}

/**
 * La cause, dite à l'agent — LA formulation, partagée par tous les reçus :
 * {@link decrireLExportEnCours} pour les actions passées par `ctx.mutate`, et
 * `documentWriteExecutor.ts` pour `write_markdown`, qui n'a pas de bilan chiffré
 * (sa reprise vit dans le point d'écriture).
 *
 * ⚠️ « Un export », pas « l'enregistrement automatique » : un téléchargement
 * produit exactement le même refus, et l'éditeur ne dit pas qui exporte.
 */
export const CAUSE_EXPORT_EN_COURS = 'an export of the document is in progress (autosave or download); the editor is '
  + 'read-only while it exports';

/** La cause chiffrée d'une action passée par `ctx.mutate` : nombre de refus et attente cumulée. */
export function decrireLExportEnCours(bilan: { refus: number; attenteMs: number }): string {
  return `the editor refused the write ${bilan.refus} time(s) over ${bilan.attenteMs} ms because ${CAUSE_EXPORT_EN_COURS}`;
}

/**
 * Vrai si le fournisseur a rendu un échec explicite, sous l'une de ses deux
 * formes : `{success:false}` (toutes les écritures) ou `{status:'rejected'}`
 * (`history.undo` / `redo`). Un verbe qui juge un résultat de `history.*` doit
 * passer par ici : ne lire que `success` laisse passer l'annulation rejetée.
 */
export function estUnEchecDuFournisseur(resultat: unknown): boolean {
  return estUnObjet(resultat) && (resultat.success === false || resultat.status === 'rejected');
}

/**
 * Ouvre le réessai d'une action.
 *
 * @param dependances.lireLeMode - Lecture FRAÎCHE du mode du document, relue à
 *   chaque refus (voir {@link refusPassagerSousCeMode}).
 * @param dependances.avantReprise - Appelée après chaque attente, avant de
 *   rejouer : c'est là que l'appelant ré-asserte l'époque du montage. Si elle
 *   jette, l'appel n'est pas rejoué.
 */
export function creerReessaiSurRefusPassager(dependances: {
  lireLeMode: () => CanvasDocumentMode;
  avantReprise: () => void;
}): ReessaiSurRefusPassager {
  let issue: IssueDesRefusPassagers | null = null;
  let refus = 0;
  let attenteMs = 0;
  let appelsAboutis = 0;

  // Une action garde la plus grave de ses issues : un appel repris avec succès
  // ne doit pas effacer la borne dépassée par un autre.
  const noter = (constatee: IssueDesRefusPassagers): void => {
    if (issue === null || GRAVITE[constatee] > GRAVITE[issue]) issue = constatee;
  };

  const executer = async <T>(tentative: () => PeutEtreUnePromesse<T>): Promise<T> => {
    for (let reprise = 0; ; reprise += 1) {
      const resultat = await tentative();
      if (raisonDuRefusPassager(resultat) === undefined) {
        appelsAboutis += 1;
        if (reprise > 0) noter(estUnEchecDuFournisseur(resultat) ? 'other_failure' : 'recovered');
        return resultat;
      }
      refus += 1;
      if (refusPassagerSousCeMode(resultat, dependances.lireLeMode) === undefined) {
        noter('durable_read_only');
        return resultat;
      }
      const restant = BUDGET_D_ATTENTE_DU_REFUS_PASSAGER_MS - attenteMs;
      if (restant <= 0) {
        noter('bound_exceeded');
        return resultat;
      }
      const pause = Math.min(
        PAUSES_AVANT_REPRISE_MS[Math.min(reprise, PAUSES_AVANT_REPRISE_MS.length - 1)]!,
        restant,
      );
      await new Promise<void>((resoudre) => { setTimeout(resoudre, pause); });
      attenteMs += pause;
      dependances.avantReprise();
    }
  };

  return {
    executer,
    bilan: () => ({ issue, refus, attenteMs, appelsAboutis }),
  };
}
